import {
  type ContentStatusChange,
  type ContentType,
  type StaffRole,
  type UtcInstant,
} from '@apuestapr/contracts';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  buttonVariants,
  cn,
  icons,
  toast,
} from '@apuestapr/ui';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import {
  getDashboardContentDefinition,
  type ContentItemByType,
  type ContentStatusByType,
} from './catalog.ts';
import { useManagedContentList, type ManagedContentList, type RequestState } from './hooks.ts';
import { VisibilityBadge } from './VisibilityBadge.tsx';

/**
 * Re-exported rather than defined here since Task 13.2, when the support queue needed the same formatting.
 * The definition lives in `format/instants.ts`; this keeps the three content modules that import it from here
 * unchanged.
 */
export { formatPuertoRicoInstant, puertoRicoZoneLabel } from '../format/instants.ts';

// Also imported directly, because this module renders instants itself.
import { formatPuertoRicoInstant, puertoRicoZoneLabel } from '../format/instants.ts';

export const ContentFailureState = ({
  failure,
}: {
  readonly failure: ApiFailure;
}): ReactElement => {
  if (failure.kind === 'http' && failure.status === 403) {
    return <p role="alert">No tienes permiso para administrar contenido.</p>;
  }
  if (failure.kind === 'malformed') {
    return (
      <div role="alert">
        <p>La API respondió con datos que no coinciden con el contrato.</p>
        {failure.issues.length > 0 ? (
          <ul>
            {failure.issues.map((issue) => (
              <li key={`${issue.path}:${issue.message}`}>
                {issue.path || '(respuesta)'}: {issue.message}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
  return <p role="alert">No se pudo cargar el contenido.</p>;
};

const PublishInstant = ({ instant }: { readonly instant: UtcInstant | null }): ReactElement => (
  <span className="grid gap-1">
    {instant === null ? (
      <span>Sin fecha</span>
    ) : (
      <time dateTime={instant}>{formatPuertoRicoInstant(instant)}</time>
    )}
    <span className="text-xs text-muted-foreground">{puertoRicoZoneLabel}</span>
  </span>
);

interface DeleteContentDialogProps {
  readonly label: string;
  readonly loading: boolean;
  readonly onDelete: () => Promise<boolean>;
}

const DeleteContentDialog = ({
  label,
  loading,
  onDelete,
}: DeleteContentDialogProps): ReactElement => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive">
          Eliminar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar permanentemente “{label}”</DialogTitle>
          <DialogDescription>
            “{label}” se eliminará permanentemente. Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={loading} variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            loading={loading}
            loadingLabel={`Eliminando ${label}…`}
            onClick={() => {
              void onDelete().then((deleted) => {
                if (deleted) setOpen(false);
              });
            }}
            variant="destructive"
          >
            Eliminar permanentemente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ContentListViewProps<Type extends ContentType> {
  readonly type: Type;
  readonly staffRole: StaffRole;
  readonly list: ManagedContentList<Type>;
}

/**
 * Shaped like the table it stands in for — a caption-height line, then a handful of rows — so the
 * layout does not jump once the real list answers. Announced once via the caller's own `role="status"`,
 * the same reasoning `Skeleton` documents for why it carries no ARIA role of its own.
 */
const ContentListSkeleton = (): ReactElement => (
  <div
    aria-hidden="true"
    className="overflow-hidden rounded-xl border border-border bg-card p-4 text-card-foreground"
  >
    <Skeleton className="mb-4 h-4 w-48" />
    <div className="grid gap-3">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="flex items-center gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  </div>
);

export const ContentListView = <Type extends ContentType>({
  type,
  staffRole,
  list,
}: ContentListViewProps<Type>): ReactElement => {
  const definition = getDashboardContentDefinition(type);
  const busy = list.mutation.status === 'loading';

  if (list.state.status === 'loading')
    return (
      <>
        <p className="sr-only" role="status">
          Cargando contenido…
        </p>
        <ContentListSkeleton />
      </>
    );
  if (list.state.status === 'error') return <ContentFailureState failure={list.state.failure} />;

  const rows = list.state.data;
  return (
    <>
      {rows.length === 0 ? (
        <EmptyState
          icon={icons.layers}
          title={`No hay ${definition.label.toLowerCase()} todavía`}
          description="Crea la primera entrada de este tipo."
        />
      ) : (
        <div
          aria-label={`Tabla de ${definition.label}`}
          className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground"
          role="region"
          // Horizontal overflow is a named keyboard-reachable region, not a pointer-only viewport.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          <Table>
            <TableCaption>
              {definition.label}: {String(rows.length)}{' '}
              {rows.length === 1 ? 'elemento' : 'elementos'}, en el orden provisto por el servidor.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{definition.primaryLabel}</TableHead>
                <TableHead>Visibilidad</TableHead>
                <TableHead>Publicación</TableHead>
                <TableHead>Despublicación</TableHead>
                <TableHead>Orden</TableHead>
                <TableHead>Última edición</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const label = String(row[definition.primaryField]);
                const order = definition.orderField === null ? null : row[definition.orderField];
                const archiveInput: ContentStatusChange = {
                  status: 'archived',
                  publishAt: row.publishAt,
                  unpublishAt: row.unpublishAt,
                };
                return (
                  <TableRow key={row.id} data-content-id={row.id}>
                    <TableHead scope="row" className="min-w-48 text-foreground">
                      {label}
                    </TableHead>
                    <TableCell>
                      <VisibilityBadge visibility={row.effectiveVisibility} />
                    </TableCell>
                    <TableCell className="min-w-52">
                      <PublishInstant instant={row.publishAt} />
                    </TableCell>
                    <TableCell className="min-w-52">
                      <PublishInstant instant={row.unpublishAt} />
                    </TableCell>
                    <TableCell>{order === null ? 'Orden automático' : String(order)}</TableCell>
                    <TableCell>
                      {row.updatedBy?.nombre ?? 'Editor eliminado/no disponible'}
                    </TableCell>
                    <TableCell>
                      <div className="flex min-w-max flex-wrap gap-2">
                        <Link
                          className={cn(buttonVariants({ size: 'sm', variant: 'outline' }))}
                          to={`${String(row.id)}/edit`}
                        >
                          {/* The row's own heading already names it; a sighted reader does not need
                              it repeated on every button, but the accessible name still does, so a
                              screen reader user moving by control still hears which row this is.
                              The {' '} is explicit rather than leading whitespace inside the sr-only
                              span, because the accessible-name computation trims each element's own
                              text before concatenating and would otherwise collapse the two words
                              together. */}
                          Editar <span className="sr-only">{label}</span>
                        </Link>
                        <Link
                          className={cn(buttonVariants({ size: 'sm', variant: 'secondary' }))}
                          to={`${String(row.id)}/preview`}
                        >
                          Vista previa <span className="sr-only">de {label}</span>
                        </Link>
                        <Button
                          disabled={row.effectiveVisibility === 'archived'}
                          loading={busy}
                          loadingLabel={`Archivando ${label}…`}
                          onClick={() => {
                            void list
                              .changeStatus(row.id, archiveInput as ContentStatusByType[Type])
                              .then((result) => {
                                if (result.ok) toast({ title: `${label} archivada` });
                              });
                          }}
                          size="sm"
                          variant="destructive"
                        >
                          {row.effectiveVisibility === 'archived' ? (
                            'Archivado'
                          ) : (
                            <>
                              Archivar <span className="sr-only">{label}</span>
                            </>
                          )}
                        </Button>
                        {staffRole === 'admin' ? (
                          <DeleteContentDialog
                            label={label}
                            loading={busy}
                            onDelete={async () => {
                              const result = await list.remove(row.id);
                              if (result.ok)
                                toast({ title: `${label} eliminada`, variant: 'destructive' });
                              return result.ok;
                            }}
                          />
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {list.reconciling ? (
        <p className="sr-only" role="status">
          Actualizando visibilidad con la hora de la base de datos…
        </p>
      ) : null}
      {list.reconciliationFailure === null ? null : (
        <div className="mt-4 rounded-xl border border-border bg-card p-4" role="alert">
          <p className="m-0">
            Los cambios se guardaron, pero no se pudo actualizar la visibilidad con la hora más
            reciente de la base de datos.
          </p>
          <Button
            className="mt-3"
            onClick={() => void list.reconcile()}
            size="sm"
            variant="outline"
          >
            Reintentar actualización
          </Button>
        </div>
      )}
      {list.mutation.status === 'error' ? (
        <ContentFailureState failure={list.mutation.failure} />
      ) : null}
    </>
  );
};

export const ContentListPage = ({ type }: { readonly type: ContentType }): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const list = useManagedContentList(
    session.client,
    type,
    authenticated?.csrfToken ?? '',
    session.handleFailure,
  );
  if (authenticated === null) return <></>;
  const definition = getDashboardContentDefinition(type);
  return (
    <section aria-labelledby={`content-list-${type}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0" id={`content-list-${type}`}>
            {definition.label}
          </h2>
          <p className="mb-0 text-muted-foreground">
            Visibilidad y horarios calculados por el servidor.
          </p>
        </div>
        <Link className={buttonVariants({ variant: 'primary' })} to="new">
          Crear entrada
        </Link>
      </div>
      <ContentListView type={type} staffRole={authenticated.user.rol} list={list} />
    </section>
  );
};

/** Useful for state-focused tests and consumers that render request states independently. */
export type ContentListRequestState<Type extends ContentType> = RequestState<
  readonly ContentItemByType[Type][]
>;
